// ============================================
// Path: /frontend/src/components/layout/Footer.jsx
// Purpose: Minimal footer with expandable sheet for links
// ============================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';

export function Footer() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {/* Minimal Footer - Always Visible */}
        <SheetTrigger asChild>
          <footer className="border-t bg-gradient-to-r from-orange-50 to-amber-50 cursor-pointer hover:from-orange-100 hover:to-amber-100 transition-all">
            <div className="container py-3 sm:py-4 px-4">
              <div className="flex flex-col items-center justify-center gap-1 sm:gap-1.5">
                {/* Brand Name */}
                <div className="flex items-center gap-1.5 sm:gap-2">
                 <img src="/images/logo.png" alt="MotoHome Finder" className="h-7 w-7 sm:h-8 sm:w-8" />
                  <span className="text-xs sm:text-sm font-medium text-gray-900">
                    MotoHome Finder
                  </span>
                </div>
                
                {/* Copyright & Required Links */}
                <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-gray-700">
                  <span>&copy; {new Date().getFullYear()} All rights reserved</span>
                  <span className="hidden xs:inline text-orange-400">•</span>
                  <Link 
                    to="/terms" 
                    className="text-orange-600 hover:text-orange-700 transition-colors underline font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms
                  </Link>
                  <span className="text-orange-400">•</span>
                  <Link 
                    to="/privacy" 
                    className="text-orange-600 hover:text-orange-700 transition-colors underline font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy
                  </Link>
                </div>

                {/* Indicator to show more is available */}
                <div className="flex items-center gap-1 text-[10px] sm:text-xs text-orange-600 mt-0.5 sm:mt-1">
                  <ChevronUp className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  <span className="font-medium">More</span>
                </div>
              </div>
            </div>
          </footer>
        </SheetTrigger>

        {/* Sheet Content - Slides up from bottom */}
        <SheetContent side="bottom" className="h-auto max-h-[80vh] overflow-y-auto">
          <SheetHeader className="mb-4 sm:mb-6">
            <SheetTitle className="flex items-center gap-2 text-base sm:text-lg">
              <img src="/images/logo.png" alt="MotoHome Finder" className="h-7 w-7 sm:h-8 sm:w-8" />
              MotoHome Finder
            </SheetTitle>
          </SheetHeader>

          {/* All Footer Links in Sheet */}
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 pb-4">
            {/* Categories */}
            <div>
              <h4 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3 text-orange-700">Categories</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <Link 
                    to="/vehicles" 
                    className="text-xs sm:text-sm text-muted-foreground hover:text-orange-600 transition-colors block"
                    onClick={() => setIsOpen(false)}
                  >
                    Vehicles
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/properties" 
                    className="text-xs sm:text-sm text-muted-foreground hover:text-orange-600 transition-colors block"
                    onClick={() => setIsOpen(false)}
                  >
                    Properties
                  </Link>
                </li>
              </ul>
            </div>

            {/* About Us */}
            <div>
              <h4 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3 text-orange-700">About Us</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <Link 
                    to="/about" 
                    className="text-xs sm:text-sm text-muted-foreground hover:text-orange-600 transition-colors block"
                    onClick={() => setIsOpen(false)}
                  >
                    Our Story
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    className="text-xs sm:text-sm text-muted-foreground hover:text-orange-600 transition-colors block"
                    onClick={() => setIsOpen(false)}
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3 text-orange-700">Legal</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <Link 
                    to="/terms" 
                    className="text-xs sm:text-sm text-muted-foreground hover:text-orange-600 transition-colors block"
                    onClick={() => setIsOpen(false)}
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/privacy" 
                    className="text-xs sm:text-sm text-muted-foreground hover:text-orange-600 transition-colors block"
                    onClick={() => setIsOpen(false)}
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Tagline at bottom of sheet */}
          <div className="border-t pt-3 sm:pt-4 mt-2 sm:mt-4">
            <p className="text-[10px] sm:text-xs text-center text-muted-foreground">
              Your one-stop shop for vehicles and properties
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}